/* eslint-disable react-hooks/exhaustive-deps */
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { Pagination, Tag } from 'antd'
import { format } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetUsersV2Query, useLazyGetUsersV2Query } from '../../api/v2/users'
import MyTable from '../../components/MyTable'
import { useMyContext } from '../../context/context'
import { useGetTyposQuery } from '../../api/v2/typo'
import FilterBoolean from './filters/Boolean'
import FilterSelect from './filters/Select'
import FilterString from './filters/String'
import FilterTypos from './filters/Typos'
import { UsersProvider, useUsers } from './useUsers'
import ExportUsers from './Export'

function getTextWidth(text, fontSize) {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  context.font = `${fontSize}px`
  return context.measureText(text).width
}

function UsersTable() {
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(1)
  const [orderBy, setOrderBy] = useState([])

  const { myContext } = useMyContext()
  const requestRef = useRef(null)
  const { usersHeaders, usersFilters, setUsersFilters } = useUsers()

  const [fetchUsers, { data, isFetching }] = useLazyGetUsersV2Query()
  const { data: typos } = useGetTyposQuery(myContext)

  useEffect(() => {
    if (requestRef.current) {
      requestRef.current.abort()
    }

    requestRef.current = fetchUsers({
      ...myContext,
      page,
      pageSize,
      filters: usersFilters,
      headers: usersHeaders,
      orderBy,
    })
  }, [page, pageSize, orderBy, fetchUsers, myContext, usersFilters, usersHeaders])

  const Filter = useCallback(
    ({ m }) => {
      if (m.id === 'TYPO.id') {
        return <FilterTypos setFilters={setUsersFilters} m={m} />
      }
      if (m.values?.length > 0) {
        return <FilterSelect setFilters={setUsersFilters} m={m} />
      }
      if (m.filter === 'boolean') {
        return <FilterBoolean setFilters={setUsersFilters} m={m} />
      }

      return <FilterString setFilters={setUsersFilters} m={m} />
    },
    [setUsersFilters]
  )

  const getColumns = array =>
    array.map(m => ({
      title: () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} onClick={e => e.stopPropagation()} aria-hidden>
          <div>{m.label}</div>
          <Filter m={m} />
        </div>
      ),
      dataIndex: m.id,
      ellipsis: true,
      sorter: true,
      onHeaderCell: () => {
        return {
          style: {
            backgroundColor: m.category.color,
            color: m.category.color2,
          },
        }
      },
      render: (text, v) => {
        const minWidth = getTextWidth(m.label, 14) + 50 < 80 ? 80 : getTextWidth(m.label, 14) + 50

        const isBoolean = m.filter === 'boolean'
        const styles = { minWidth }

        if (isBoolean) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', ...styles }}>
              {text ? <CheckCircleOutlined style={{ color: 'green' }} /> : <CloseCircleOutlined style={{ color: 'red' }} />}
            </div>
          )
        }

        if (/^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d.\d{3}Z$/.test(text)) {
          return <div style={{ ...styles }}>{format(new Date(text), 'dd/MM/yyyy HH:mm')}</div>
        }

        if (m.id === 'TYPO.id') {
          return (
            <div style={{ ...styles, minHeight: 22 }} aria-hidden>
              {typos?.find(f => f.id === Number(text))?.label}
            </div>
          )
        }

        return (
          <div style={{ ...styles, minHeight: 22 }} aria-hidden>
            {text}
          </div>
        )
      },
    }))

  const columns = useMemo(() => (data ? getColumns(data.columns) : []), [data, getColumns])

  const filters = useMemo(() => {
    if (!usersFilters || !data) {
      return []
    }

    const f = {}
    for (let i = 0; i < usersFilters.length; i += 1) {
      const curr = usersFilters[i]

      if (Array.isArray(curr)) {
        for (let j = 0; j < curr.length; j += 1) {
          const curr2 = curr[j]
          if (!f[curr2.property]) {
            f[curr2.property] = true
          }
        }
      } else if (!f[curr.property]) {
        f[curr.property] = true
      }
    }

    return Object.keys(f)
      .map(m => data.columns.find(ff => ff.id === m))
      .filter(ff => ff)
      .map(m => m.label)
  }, [data, usersFilters])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
        <Tag color="processing">{`Nombre d'utilisateurs : ${data?.count || 0}`}</Tag>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {data?.labelsByCategories
            ?.filter(f => f.color)
            .filter(f => !f.value.includes('UED'))
            .map(m => {
              return (
                <Tag color={m.color} style={{ border: `1px solid ${m.color2}`, color: m.color2 }}>
                  {m.label}
                </Tag>
              )
            })}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        {filters.length > 0 && (
          <div style={{ display: 'flex', gap: 8, width: '100%', flexWrap: 'wrap', alignItems: 'center' }}>
            <Tag color="processing">Filtres : </Tag>
            {filters.map(m => (
              <Tag>{m}</Tag>
            ))}
          </div>
        )}
        <ExportUsers />
      </div>
      <div style={{ overflow: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
        <MyTable
          className="table-users"
          rowKey="U.id"
          scroll={{ y: true, x: true }}
          columns={columns}
          loading={isFetching}
          dataSource={data?.users}
          pagination={false}
          onChange={(_, __, sort) => {
            if (sort.order) {
              setOrderBy([sort.field, sort.order === 'ascend' ? 'asc' : 'desc'])
            } else {
              setOrderBy([])
            }
          }}
          sticky
          bordered
        />
        <Pagination
          size="small"
          current={page}
          total={data?.count}
          pageSize={pageSize}
          onChange={(p, pz) => {
            setPage(p)
            setPageSize(pz)
          }}
          showSizeChanger
        />
      </div>
    </div>
  )
}

export default function Users() {
  return (
    <UsersProvider>
      <UsersTable />
    </UsersProvider>
  )
}
